import { z } from "zod";

export const adsManagementOptions = [
  "self",
  "agency",
  "freelancer",
  "none",
] as const;

export const monthlyAdSpendOptions = [
  "none",
  "under_300",
  "300_1000",
  "1000_3000",
  "over_3000",
] as const;

export const productCategoryOptions = [
  "apparel",
  "footwear",
  "accessories",
  "other",
] as const;

export const fashionFitOptions = ["fashion", "not_fashion", "unknown"] as const;

export type ProductCategory = (typeof productCategoryOptions)[number];
export type FashionFit = (typeof fashionFitOptions)[number];

/**
 * Marketing attribution read from the browser when the lead is submitted, so
 * sales can tell which channel produced each lead without cross-referencing
 * analytics.
 */
export const LeadSourceSchema = z.object({
  utmSource: z.string().max(200).optional(),
  utmMedium: z.string().max(200).optional(),
  utmCampaign: z.string().max(200).optional(),
  utmContent: z.string().max(200).optional(),
  utmTerm: z.string().max(200).optional(),
  referrer: z.string().max(500).optional(),
});

export type LeadSource = z.infer<typeof LeadSourceSchema>;

/**
 * Step one of the lead form: the minimum needed to follow up on WhatsApp.
 * Everything else is asked after the lead is already stored.
 */
export const LeadSchema = z.object({
  name: z.string().trim().min(1, { message: "Ingresa tu nombre" }),
  whatsapp: z
    .string()
    .trim()
    .min(1, { message: "Ingresa tu WhatsApp" })
    .refine((value) => (value.match(/\d/g) ?? []).length >= 6, {
      message: "Ingresa un número de WhatsApp válido",
    }),
  website: z
    .string()
    .trim()
    .min(1, { message: "Ingresa la web de tu tienda" })
    .max(300),
  productCategory: z
    .string()
    .min(1, { message: "Contanos qué vendés" })
    .pipe(z.enum(productCategoryOptions)),
});

export type LeadInput = z.infer<typeof LeadSchema>;

export type LeadSubmission = LeadInput & {
  source?: LeadSource;
  honeypot?: string;
  pageUrl?: string;
};

/**
 * Step two of the lead form: who runs ads and roughly how much they spend.
 */
export const LeadDetailsSchema = z.object({
  adsManagement: z.enum(adsManagementOptions, {
    message: "Selecciona una opción",
  }),
  monthlyAdSpend: z.enum(monthlyAdSpendOptions, {
    message: "Selecciona un rango",
  }),
});

export type LeadDetailsInput = z.infer<typeof LeadDetailsSchema>;

export type LeadDetailsSubmission = Partial<LeadDetailsInput> & {
  leadPublicId: string;
};

export const adsManagementLabels: Record<
  (typeof adsManagementOptions)[number],
  string
> = {
  self: "Yo o alguien del equipo",
  agency: "Una agencia",
  freelancer: "Un freelancer",
  none: "Todavía no hacemos ads",
};

export const adsManagementSelectOptions = (
  Object.entries(adsManagementLabels) as [
    (typeof adsManagementOptions)[number],
    string,
  ][]
).map(([value, label]) => ({ value, label }));

export const monthlyAdSpendLabels: Record<
  (typeof monthlyAdSpendOptions)[number],
  string
> = {
  none: "Todavía no invierto",
  under_300: "Menos de USD 300",
  "300_1000": "USD 300–1.000",
  "1000_3000": "USD 1.000–3.000",
  over_3000: "Más de USD 3.000",
};

export const monthlyAdSpendSelectOptions = (
  Object.entries(monthlyAdSpendLabels) as [
    (typeof monthlyAdSpendOptions)[number],
    string,
  ][]
).map(([value, label]) => ({ value, label }));

export const productCategoryLabels: Record<ProductCategory, string> = {
  apparel: "Ropa",
  footwear: "Calzado",
  accessories: "Accesorios de moda",
  other: "Otra categoría",
};

export const productCategorySelectOptions = (
  Object.entries(productCategoryLabels) as [ProductCategory, string][]
).map(([value, label]) => ({ value, label }));

export function isFashionProductCategory(category: ProductCategory) {
  return category !== "other";
}

/**
 * Meta optimization should only learn from confirmed fashion stores.
 * Parked, empty, social-only, or otherwise unreadable sites stay unknown
 * for sales follow-up, but they are not a Lead conversion.
 */
export function shouldCaptureMetaLead(fit: FashionFit) {
  return fit === "fashion";
}

export type FashionStoreClassification = {
  fit: FashionFit;
  reason: string;
  source: "form" | "website";
};

/**
 * Self-reported "otra categoría" is enough to reject. A not-fashion website
 * overrides a fashion answer. Unreadable or social-only sites stay unknown
 * so sales can still follow up without treating them as Meta conversions.
 */
export function resolveFashionFit(input: {
  productCategory: ProductCategory;
  websiteFit: FashionFit;
  websiteReason: string;
}): FashionStoreClassification {
  if (!isFashionProductCategory(input.productCategory)) {
    return {
      fit: "not_fashion",
      reason: "El visitante indicó que no vende moda.",
      source: "form",
    };
  }

  if (input.websiteFit === "not_fashion") {
    return {
      fit: "not_fashion",
      reason: input.websiteReason,
      source: "website",
    };
  }

  if (input.websiteFit === "fashion") {
    return {
      fit: "fashion",
      reason: input.websiteReason,
      source: "website",
    };
  }

  return {
    fit: "unknown",
    reason: input.websiteReason,
    source: "website",
  };
}
